import { useCallback } from 'react'

import { isInputConfig } from '../../../../application/formBuilder/config'
import { usePolicy } from '../../../../application/product/policy/PolicyProvider'
import {
  Data,
  getInsuredProfile,
  getPolicyVersion,
  PolicyAdjustmentRequest,
  PolicyChangeType,
  Profile,
  ProfileDisclosureChange,
} from '../../../../domain'
import { useAdjustments } from '../../../../state'
import {
  ChangeType,
  FormConfig,
  ProfileChangeConfig,
} from '../../../../state/configuration'
import { hasUpdates, toUpdates } from '../../helpers'

const toProfileReducer =
  (existingData: Data, data: Data, { profileCode }: Profile) =>
  (
    policyAdjustment: PolicyAdjustmentRequest,
    field: string,
    adjustment: ProfileChangeConfig,
  ): PolicyAdjustmentRequest => {
    const updates = toUpdates(existingData, data)

    const changes: ProfileDisclosureChange[] = updates(field, adjustment).map(
      (update) => ({
        changeType: PolicyChangeType.PROFILE_DISCLOSURE_CHANGE,
        profileCode,
        update,
      }),
    )

    return {
      ...policyAdjustment,
      changes: [...policyAdjustment.changes, ...changes],
    }
  }

export const useAdditionalProfilePolicyAdjustmentRequest = (
  form: FormConfig,
  profile: Profile,
) => {
  const { policy } = usePolicy()
  const { getAdjustment } = useAdjustments()

  return useCallback(
    (
      existingData: Data,
      updatedData: Data,
      effectiveAt?: string,
    ): PolicyAdjustmentRequest => {
      const { quote } = getPolicyVersion(policy)

      const profileReducer = toProfileReducer(existingData, updatedData, profile)

      return form
        .filter(isInputConfig)
        .filter(hasUpdates(existingData, updatedData))
        .reduce(
          (proposalAdjustment: PolicyAdjustmentRequest, input) => {
            const adjustment = getAdjustment(input.name)

            switch (adjustment?.changeType) {
              case ChangeType.PROFILE_DISCLOSURE:
                return profileReducer(proposalAdjustment, input.name, adjustment)
              default:
                throw new Error(
                  `AdjustmentChangeType ${adjustment.changeType} not supported by this`,
                )
            }
          },
          {
            policyId: policy.policyId,
            adjustmentType: 'MTA',
            changes: !getInsuredProfile(quote, profile.profileCode)
              ? [
                  {
                    changeType: PolicyChangeType.ADDITIONAL_PROFILE_CREATE_CHANGE,
                    profileCode: profile.profileCode,
                  },
                ]
              : [],
            ...(effectiveAt && { effectiveAt }),
          },
        )
    },
    [form, policy, profile, getAdjustment],
  )
}
